import jsQR, { QRCode } from "jsqr"
import React, { useEffect, useRef, useState } from "react"

const ScanQRCodeComponent: React.FC<{
  width: string
  height: string
  readAfterFunction: (code: QRCode) => Promise<unknown>
  facingMode?: ConstrainDOMString | undefined
}> = (props) => {
  const RefVideo = useRef<HTMLVideoElement>(null!)
  const RefStream = useRef<MediaStream>(null!)
  const RefCanvas = useRef<HTMLCanvasElement>(null!)
  const loginTimeOutRef = useRef<NodeJS.Timeout>()

  const [QRCode, setQRCode] = useState<QRCode | null>()
  const [isSearching, setIsSearching] = useState(false)

  const startTick = () => {
    if (RefVideo.current == null || RefCanvas.current == null) return

    if (RefVideo.current.readyState === RefVideo.current.HAVE_ENOUGH_DATA) {
      RefCanvas.current.height = RefVideo.current.videoHeight
      RefCanvas.current.width = RefVideo.current.videoWidth
      const ctx = RefCanvas.current.getContext("2d")
      if (ctx == null) return
      ctx.drawImage(
        RefVideo.current,
        0,
        0,
        RefCanvas.current.width,
        RefCanvas.current.height
      )
      const img = ctx.getImageData(
        0,
        0,
        RefCanvas.current.width,
        RefCanvas.current.height
      )
      const code: QRCode | null = jsQR(img.data, img.width, img.height, {
        inversionAttempts: "dontInvert",
      })
      code && drawLine(ctx, code.location)
      if (code && code.data != null && code.data !== "") {
        if (loginTimeOutRef.current) {
          clearTimeout(loginTimeOutRef.current)
        }

        loginTimeOutRef.current = setTimeout(async () => {
          setQRCode(code)
        }, 100)
      }
    }
    setTimeout(startTick, 1000 / 60)
  }

  useEffect(() => {
    if (!navigator || !navigator.mediaDevices) return
    navigator.mediaDevices
      .getUserMedia({
        audio: false,
        video: {
          facingMode: props.facingMode ?? "user",
        },
      })
      .then(async (stream) => {
        if (RefVideo.current == null) return
        RefStream.current = stream
        RefVideo.current.srcObject = RefStream.current
        RefVideo.current.playsInline = true
        RefVideo.current.play()
        startTick()
      })
      .catch((error) => console.log(error))

    return () => {
      if (RefStream.current == null) return
      const tracks = RefStream.current.getTracks()
      if (tracks == null || tracks.length <= 0) return
      tracks.forEach((track) => {
        track.stop()
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (QRCode == null) return
    if (isSearching) return

    setIsSearching(true)
    props.readAfterFunction(QRCode).then(() => {
      setQRCode(undefined)
      setIsSearching(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [QRCode])

  return (
    <>
      <video
        ref={RefVideo}
        style={{
          display: QRCode != null ? "none" : "block",
          width: props.width,
          height: props.height,
        }}></video>
      <canvas
        style={{
          display: QRCode != null ? "block" : "none",
          width: props.width,
          height: props.height,
        }}
        ref={RefCanvas}></canvas>
    </>
  )
}

export default ScanQRCodeComponent

const drawLine = (
  ctx: CanvasRenderingContext2D,
  pos: QRCode["location"],
  options = { color: "red", size: 5 }
) => {
  ctx.strokeStyle = options.color
  ctx.lineWidth = options.size

  ctx.beginPath()
  ctx.moveTo(pos.topLeftCorner.x, pos.topLeftCorner.y)
  ctx.lineTo(pos.topRightCorner.x, pos.topRightCorner.y)
  ctx.lineTo(pos.bottomRightCorner.x, pos.bottomRightCorner.y)
  ctx.lineTo(pos.bottomLeftCorner.x, pos.bottomLeftCorner.y)
  ctx.lineTo(pos.topLeftCorner.x, pos.topLeftCorner.y)
  ctx.stroke()
}
